// Global state — single source of truth. Zero sibling imports.

export let sessionId = null;
export let documentId = null;
export let cachedTree = null;
export let lastScan = null;
export let currentSelection = "";
export let selectionMeta = null;
export let pinnedSelection = null;
export let isBusy = false;
export let documentReady = false;
export let showDebugLogs = false;
export let suggestEdits = false;
export let chatHistoryOffset = 0;
export let appliedEdits = []; // { id, nodeId, originalText, newText, reason, timestamp, undone }

export let chatMode = null; // null | 'summarize' | 'audit' | 'negotiations'
export function setChatMode(v) { chatMode = v; }

export let jurisdiction = "CTH";
export function setJurisdiction(v) {
  jurisdiction = v;
  saveToSettings("miai_jurisdiction", v);
}

export let isDarkTheme = false;
// persist=false: auto-detected (no preference saved); persist=true: user explicitly set
export function setDarkTheme(v, persist = false) {
  isDarkTheme = v;
  document.body.setAttribute("data-theme", v ? "dark" : "light");
  if (persist) {
    saveToSettings("miai_dark_theme", v);
    saveToSettings("miai_theme_user_set", true);
  }
}

// Sync state
export let originalNodeMap = {};    // frozen after initial scan
export let activeNodeMap = {};      // current working copy
export let lastSyncedNodeMap = {};  // snapshot at last successful sync
export let lastSyncedAt = null;     // timestamp (ms)
export let autoSyncEnabled = true;

export function setSessionId(v) {
  sessionId = v;
}
export function setOriginalNodeMap(m) { originalNodeMap = m; }
export function setActiveNodeMap(m)   { activeNodeMap = m; }
export function setLastSyncedNodeMap(m){ lastSyncedNodeMap = m; }
export function setLastSyncedAt(ts)   { lastSyncedAt = ts; }
export function setAutoSyncEnabled(v) { autoSyncEnabled = v; }

export function computePendingDelta() {
  const added = [], removed = [];
  for (const [id, node] of Object.entries(activeNodeMap)) {
    if (!lastSyncedNodeMap[id]) added.push(node);
  }
  for (const id of Object.keys(lastSyncedNodeMap)) {
    if (!activeNodeMap[id]) removed.push(id);
  }
  return { added, removed };
}

export function pendingDeltaCount() {
  const { added, removed } = computePendingDelta();
  return added.length + removed.length;
}
export function setDocumentId(v) {
  documentId = v;
}
export function setCachedTree(v) {
  cachedTree = v;
}
export function setLastScan(v) {
  lastScan = v;
}
export function setCurrentSelection(v) {
  currentSelection = v;
}
export function setSelectionMeta(v) {
  selectionMeta = v;
}
export function setPinnedSelection(v) {
  pinnedSelection = v;
}
export function setShowDebugLogs(v) {
  showDebugLogs = v;
}
export function setSuggestEdits(v) {
  suggestEdits = v;
}
export function setChatHistoryOffset(v) {
  chatHistoryOffset = v;
}

// Update sync status — reflected in the header status area
export function updateSyncStatus(state) {
  if (state === "syncing") {
    setStatus("Syncing…", "loading");
  } else if (state === "synced") {
    setStatus("Ready", "ready");
  } else if (state === "pending") {
    setStatus("Syncing…", "loading");
  } else if (state === "error") {
    setStatus("Sync error", "error");
  }
}

// Update header status dot + label
export function setStatus(text, level = "") {
  const dot = document.getElementById("header-status-dot");
  const label = document.getElementById("header-status-label");
  if (dot) {
    dot.className = "header-status-dot" + (level ? ` ${level}` : "");
  }
  if (label) {
    label.textContent = text;
    label.className = "header-status-label" + (level ? ` ${level}` : "");
  }
}

export function setBusy(busy) {
  isBusy = busy;
  syncButtonStates();
}

export function setDocumentReady(ready) {
  documentReady = ready;
  syncButtonStates();
}

export function toggleSuggestEdits() {
  suggestEdits = !suggestEdits;
  const btn = document.getElementById("suggest-edits-toggle");
  if (btn) btn.classList.toggle("active", suggestEdits);
  saveToSettings("suggestEdits", suggestEdits);
}

// Disable buttons when document not ready or busy; Ask also needs input
export function syncButtonStates() {
  const inputVal = (document.getElementById("user-input")?.value || "").trim();
  const canInteract = documentReady && !isBusy;

  document.getElementById("get-selection-btn").disabled = !canInteract;
  document.getElementById("ask-btn").disabled = !canInteract || !inputVal;
}

// Office.Settings persistence
export function saveToSettings(key, value) {
  try {
    Office.context.document.settings.set(key, value);
    Office.context.document.settings.saveAsync();
  } catch (e) {
    console.warn("[Settings] save failed:", e);
  }
}

export function loadFromSettings(key) {
  try {
    return Office.context.document.settings.get(key);
  } catch (e) {
    console.warn("[Settings] load failed:", e);
    return null;
  }
}

// Find a node in the last scan by ID
export function findNodeInScan(nodeId) {
  if (!lastScan || !lastScan.nodes) return null;
  return lastScan.nodes.find((n) => n.id === nodeId) || null;
}
